import { API } from 'aws-amplify';
import gql from 'graphql-tag';
import { GraphQLResult } from '@aws-amplify/api-graphql';

export interface KPIRule {
  id: string;
  title: string;
  abbreviation: string;
  order?: number;
  action?: string;
  sourceType: string;
  triggerID?: string;
  format: string;
  formula: string;
  isHidden: boolean;
  categoryID: string;
  dimensions?: string;
  stage?: {
    title: string;
    isDeleted?: boolean;
  };
  disposition?: {
    title: string;
  };
  goals: {
    items: Array<{ id: string }>;
  };
}

interface StageCategory {
  id: string;
  title: string;
  flexAttributes: string;
}

export const KPIRepository = {
  /**
   * Fetch KPI rules for an organization
   * @param {string} orgID - Organization ID
   * @returns {Promise<Array<KPIRule>>} - List of KPI rules
   */
  fetchKPIRules: async (orgID: string): Promise<KPIRule[]> => {
    const myQuery = gql`
      query MyQuery($orgID: ID!, $nextToken: String = null) {
        listKPIRules(
          filter: { orgID: { eq: $orgID }, isDeleted: { eq: false } }
          limit: 10000
          nextToken: $nextToken
        ) {
          nextToken
          items {
            id
            title
            abbreviation
            order
            action
            sourceType
            triggerID
            format
            formula
            isHidden
            categoryID
            dimensions
            isHidden
            stage {
              title
              isDeleted
            }
            disposition {
              title
            }
          }
        }
      }
    `;

    let nextToken: string | null = null;
    const KPIRules: KPIRule[] = [];

    try {
      do {
        // Type the GraphQL result
        const listKPIRulesRet = (await API.graphql({
          query: myQuery as any,
          variables: { orgID, nextToken },
        })) as GraphQLResult<{
          listKPIRules: {
            items: KPIRule[];
            nextToken: string | null;
          };
        }>;

        if (listKPIRulesRet.data) {
          KPIRules.push(...listKPIRulesRet.data.listKPIRules.items);
          nextToken = listKPIRulesRet.data.listKPIRules.nextToken;
        } else {
          // Handle the case where the result is an Observable
          throw new Error('Unexpected GraphQL result format');
        }
      } while (nextToken !== null);

      return KPIRules;
    } catch (error) {
      console.error('Error fetching KPI rules:', error);
      throw error;
    }
  },

  /**
   * Fetch stage categories for an organization
   * @param {string} orgID - Organization ID
   * @returns {Promise<Array<StageCategory>>} - List of stage categories
   */
  fetchStageCategories: async (orgID: string): Promise<StageCategory[]> => {
    const myCategoryQuery = gql`
      query MyQuery($orgID: ID!, $nextToken: String = null) {
        listStageCategorys(
          filter: { orgID: { eq: $orgID }, isDeleted: { eq: false } }
          limit: 10000
          nextToken: $nextToken
        ) {
          nextToken
          items {
            id
            title
            flexAttributes
          }
        }
      }
    `;

    let nextToken: string | null = null;
    const categories: StageCategory[] = [];

    try {
      do {
        const listStageCategorysRet = (await API.graphql({
          query: myCategoryQuery as any,
          variables: { orgID, nextToken },
        })) as GraphQLResult<{
          listStageCategorys: {
            items: StageCategory[];
            nextToken: string | null;
          };
        }>;

        if (listStageCategorysRet.data) {
          categories.push(
            ...listStageCategorysRet.data.listStageCategorys.items
          );
          nextToken = listStageCategorysRet.data.listStageCategorys.nextToken;
        } else {
          // Handle the case where the result is an Observable
          throw new Error('Unexpected GraphQL result format');
        }
      } while (nextToken !== null);

      return categories;
    } catch (error) {
      console.error('Error fetching stage categories:', error);
      throw error;
    }
  },

  /**
   * Find KPI rules that depend on a specific disposition
   * @param {string} orgID - Organization ID
   * @param {string} dispositionID - Disposition ID
   * @returns {Promise<KPIRule[]>} - List of KPI rules that depend on the disposition
   */
  findKPIRulesForDisposition: async (
    orgID: string,
    dispositionID: string
  ): Promise<KPIRule[]> => {
    const query = gql`
      query GetKPIRulesBySourceTypeAndTrigger(
        $orgID: ID!
        $sourceType: String!
        $triggerID: ID!
      ) {
        listKPIRules(
          filter: {
            orgID: { eq: $orgID }
            isDeleted: { eq: false }
            sourceType: { eq: $sourceType }
            triggerID: { eq: $triggerID }
          }
          limit: 10000
        ) {
          items {
            id
            title
            abbreviation
            format
            formula
            isHidden
            categoryID
          }
        }
      }
    `;

    try {
      const result = (await API.graphql({
        query: query as any,
        variables: {
          orgID,
          sourceType: 'disposition',
          triggerID: dispositionID,
        },
      })) as GraphQLResult<{
        listKPIRules: {
          items: KPIRule[];
        };
      }>;

      if (result.data) {
        return result.data.listKPIRules.items;
      } else {
        throw new Error('Unexpected GraphQL result format');
      }
    } catch (error) {
      console.error(
        `Error finding KPI rules for disposition ${dispositionID}:`,
        error
      );
      return [];
    }
  },
};

export default KPIRepository;
